/**
 * `useAgentStream` — drives the agentic search SSE endpoint.
 *
 * `POST /search/agent` streams `AgentEvent` frames (SSE) rather than a single
 * JSON body, so we cannot use a plain TanStack Query fetch. Instead a
 * `useMutation` kicks off the request and we consume the `ReadableStream`
 * body, parsing frames incrementally and surfacing assembled answer text +
 * validated citations as local state for the chat panel to render.
 *
 * Every run carries an `x-client-request-id` for log correlation.
 */
import {
  AgentEventSchema,
  type AgentCitation,
  type AgentEvent,
  type AgentStopReason,
} from "@records-monitor/shared";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { paramsToFilters, type SearchParams } from "../urlState";
import { newClientRequestId } from "./useSearch";
import { useIdToken } from "@/auth/AuthProvider";

const API_BASE: string = ((): string => {
  const meta = import.meta as { env?: { VITE_API_BASE_URL?: string } };
  return meta.env?.VITE_API_BASE_URL ?? "/api";
})();

export interface AgentStreamState {
  /** Assembled answer text (concatenated deltas). */
  answer: string;
  /** Validated citations seen so far, keyed by marker. */
  citations: AgentCitation[];
  /** Why the run stopped, once it has ended. */
  stopReason: AgentStopReason | null;
  /** A non-fatal error message surfaced mid-stream, if any. */
  errorMessage: string | null;
}

const EMPTY_STATE: AgentStreamState = {
  answer: "",
  citations: [],
  stopReason: null,
  errorMessage: null,
};

/**
 * Parse the SSE wire format into validated {@link AgentEvent}s. Each event is
 * a `data:` line carrying the JSON encoding of the event (the payload is
 * self-describing via its `type` field, so we can ignore the `event:` line).
 */
function parseFrames(buffer: string): { events: AgentEvent[]; rest: string } {
  const events: AgentEvent[] = [];
  const segments = buffer.split("\n\n");
  // The final segment may be a partial frame; keep it buffered.
  const rest = segments.pop() ?? "";
  for (const segment of segments) {
    for (const line of segment.split("\n")) {
      if (!line.startsWith("data:")) continue;
      const json = line.slice("data:".length).trim();
      if (!json) continue;
      try {
        const parsed = AgentEventSchema.parse(JSON.parse(json));
        events.push(parsed);
      } catch {
        // reason: ignore malformed/foreign frames rather than aborting the run.
      }
    }
  }
  return { events, rest };
}

export interface UseAgentStreamResult extends AgentStreamState {
  /** Start a run for the current params. */
  ask: (params: SearchParams) => void;
  /** True while the stream is open / a run is in flight. */
  isStreaming: boolean;
  /** True once a run has produced a terminal `end` event. */
  isDone: boolean;
}

export function useAgentStream(): UseAgentStreamResult {
  const getToken = useIdToken();
  const [state, setState] = useState<AgentStreamState>(EMPTY_STATE);
  const [isDone, setIsDone] = useState(false);
  // Citations are deduped by marker as they stream in.
  const seenMarkers = useRef<Set<number>>(new Set());

  const apply = useCallback((event: AgentEvent): void => {
    switch (event.type) {
      case "delta": {
        setState((prev) => ({ ...prev, answer: prev.answer + event.text }));
        break;
      }
      case "cite": {
        if (!seenMarkers.current.has(event.citation.marker)) {
          seenMarkers.current.add(event.citation.marker);
          setState((prev) => ({ ...prev, citations: [...prev.citations, event.citation] }));
        }
        break;
      }
      case "error": {
        setState((prev) => ({ ...prev, errorMessage: event.message }));
        break;
      }
      case "end": {
        setState((prev) => ({
          answer: event.answer || prev.answer,
          citations: event.citations.length > 0 ? event.citations : prev.citations,
          stopReason: event.stopReason,
          errorMessage: prev.errorMessage,
        }));
        setIsDone(true);
        break;
      }
      // "start" and "tool_call" carry telemetry only; nothing to render here.
      default:
        break;
    }
  }, []);

  const mutation = useMutation({
    mutationFn: async (params: SearchParams): Promise<void> => {
      const filters = paramsToFilters(params);
      const headers = new Headers({
        "content-type": "application/json",
        accept: "text/event-stream",
        "x-client-request-id": newClientRequestId(),
      });
      const token = await getToken();
      if (token) headers.set("authorization", `Bearer ${token}`);

      const response = await fetch(`${API_BASE}/search/agent`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          q: params.q?.trim() ?? "",
          ...(filters ? { filters } : {}),
        }),
      });

      if (!response.ok || !response.body) {
        throw new Error(`Agent run failed (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const { events, rest } = parseFrames(buffer);
        buffer = rest;
        for (const event of events) apply(event);
      }
      // Flush any trailing complete frame.
      const { events } = parseFrames(buffer + "\n\n");
      for (const event of events) apply(event);
    },
  });

  const ask = useCallback(
    (params: SearchParams): void => {
      seenMarkers.current = new Set();
      setState(EMPTY_STATE);
      setIsDone(false);
      mutation.mutate(params);
    },
    [mutation],
  );

  return {
    ...state,
    ask,
    isStreaming: mutation.isPending,
    isDone,
  };
}
